import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, Skeleton, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { ROUTE_STRING } from 'constants/RouteString';
import usePollPipelineRun from 'redux/slices/pipelines/hooks/usePollPipelineRun';
import { PipelineRunStatus } from 'redux/slices/pipelines/constants/pipelineRunStatus';
import { resolveRunBranchDisplay } from 'redux/slices/pipelines/helpers/resolveRunBranchDisplay';
import PipelineRunStatusChip from './PipelineRunStatusChip';

// Poll-based live run-status view (AC-13/14/15/16). Polling itself lives in
// usePollPipelineRun (useInterval-based); this component only renders the result and
// exposes an aria-live region so status transitions are announced to assistive tech
// without requiring focus to be on it (NFR-Accessibility).
//
// `onRunChange` (optional, additive) — fires whenever the polled `run` object changes.
// Added for the pipeline detail page (AC-9/AC-10/AC-12): it needs the live `run.Status` /
// `run.ImageId` to pin the flow's Build/Deploy nodes, but must NOT stand up a second
// poller to get it — this callback threads the ALREADY-polled value up to the parent
// instead. Existing callers (e.g. RunDetail) don't pass it and are unaffected.
//
// `hideDetails` (optional, additive, default false) — deploy-pipelines-detail-console
// (AC-17): the redesigned pipeline detail page renders its OWN outcome header (status
// chip + duration + timestamp), failure banner and stage timeline from the threaded-up
// `run`, so this component's usual visible body would be a duplicate. When `hideDetails`
// is true, this component still owns the ONE poller and still threads `run` up via
// `onRunChange` — it just renders nothing itself (RunDetail, the only other caller, never
// passes this prop and is completely unaffected).
const RUN_NOT_FOUND_MESSAGE = 'This run is no longer available — the pipeline or run may have been deleted.';

const PipelineRunStatusView = ({ pipelineId, runId, onRunChange, hideDetails }) => {
	const { run, isLoading, pollError, notFound, isTerminal } = usePollPipelineRun({
		pipelineId,
		runId
	});

	useEffect(() => {
		onRunChange?.(run);
		// onRunChange identity is expected to be stable (useState setter / useCallback at the
		// call site) — only `run` should retrigger this.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [run]);

	// `hideDetails` suppresses the DUPLICATE body (chip / timestamps / error) — never a signal
	// the parent cannot render for itself. Both survive it: a run that vanished mid-poll, and
	// a failing poll (which would otherwise leave the parent showing a stale status with no
	// indication live updates had stopped — strictly worse than the page this replaced). The
	// caller renders this above its own content region, so pad to match.
	if (hideDetails) {
		if (!notFound && !pollError) {
			return null;
		}
		return (
			<Box px={4} pt={3}>
				<Alert severity="warning" variant="outlined">
					{notFound ? RUN_NOT_FOUND_MESSAGE : pollError}
				</Alert>
			</Box>
		);
	}

	// The full-page treatment for the only other caller (RunDetail), which has no back
	// affordance of its own — the redesigned detail page does, hence the leaner branch above.
	if (notFound) {
		return (
			<Box>
				<Alert severity="warning">{RUN_NOT_FOUND_MESSAGE}</Alert>
				<Box mt={2}>
					<Button component={Link} to={ROUTE_STRING.PIPELINE.LIST} variant="outlined">
						Back to Pipelines
					</Button>
				</Box>
			</Box>
		);
	}

	if (isLoading && !run) {
		return <Skeleton variant="rounded" height={120} />;
	}

	return (
		<Box display="flex" flexDirection="column" gap={2}>
			{/* aria-live="polite": announces Building -> Deploying -> Succeeded/Failed
          transitions without moving focus (NFR-Accessibility). */}
			<Box role="status" aria-live="polite" display="flex" alignItems="center" gap={2}>
				<Typography variant="subtitle1">Run status:</Typography>
				<PipelineRunStatusChip status={run?.Status} />
				{!isTerminal && (
					<Typography variant="body2" color="text.secondary">
						Updating automatically…
					</Typography>
				)}
			</Box>

			{pollError && (
				<Alert severity="warning" variant="outlined">
					{pollError}
				</Alert>
			)}

			{run?.Status === PipelineRunStatus.Failed && run?.ErrorMessage && <Alert severity="error">{run.ErrorMessage}</Alert>}

			{/* deploy-pipelines-run-cancel (Wave 5, AC-25/OQ-D) — the same `ErrorMessage` field
			    carries a cancelled run's reason. `severity="info"` (never "error") is the neutral
			    treatment: a deliberate cancel is not a failure. */}
			{run?.Status === PipelineRunStatus.Cancelled && run?.ErrorMessage && <Alert severity="info">Cancelled: {run.ErrorMessage}</Alert>}

			{run?.StartedAt && (
				<Typography variant="body2" color="text.secondary">
					Started: {new Date(run.StartedAt).toLocaleString()}
				</Typography>
			)}
			{run?.CompletedAt && (
				<Typography variant="body2" color="text.secondary">
					Completed: {new Date(run.CompletedAt).toLocaleString()}
				</Typography>
			)}
			{/* deploy-pipelines-run-overrides (Wave 4, AC-10) — this run's OWN branch, read on this
			    single-run detail page. */}
			{run && (
				<Typography variant="body2" color="text.secondary">
					Branch: {resolveRunBranchDisplay(run.BranchName)}
				</Typography>
			)}
		</Box>
	);
};

export default React.memo(PipelineRunStatusView);

PipelineRunStatusView.propTypes = {
	pipelineId: PropTypes.string.isRequired,
	runId: PropTypes.string.isRequired,
	onRunChange: PropTypes.func,
	hideDetails: PropTypes.bool
};
